/**
 * useAnalytics Hook
 * 
 * Privacy-focused analytics tracking for the app.
 * 
 * Features:
 * - Automatic session management
 * - Page view tracking
 * - Feature usage tracking
 * - Event batching for efficiency
 * - No PII collection
 * 
 * Usage:
 * const { trackEvent, trackPageView, trackFeature } = useAnalytics();
 * 
 * // Track a page view (automatic with useEffect in pages)
 * trackPageView('/portfolios/1');
 * 
 * // Track a feature usage
 * trackFeature('mortgage_calculate', { propertyId: 123 });
 * 
 * // Track a custom event
 * trackEvent('button_click', 'engagement', { button: 'save' });
 */

import { useCallback, useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useAuthStore } from '../store/authStore';
import { logger } from '../shared/utils/logger';

// Get API URL
const getApiBaseUrl = () => {
  if (process.env.EXPO_PUBLIC_API_URL) return process.env.EXPO_PUBLIC_API_URL;
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return 'https://api.farmerjohn.pacayagroup.com';
  }
  return 'http://localhost:3001';
};

// Types
type EventCategory = 'general' | 'navigation' | 'feature' | 'engagement' | 'error' | 'conversion';

type EventType = 
  // Navigation
  | 'page_view' | 'page_leave'
  // Auth
  | 'login' | 'logout' | 'session_start' | 'session_end'
  // Features
  | 'portfolio_view' | 'property_view' | 'property_create' | 'property_update'
  | 'due_diligence_start' | 'due_diligence_item_complete' | 'due_diligence_complete'
  | 'mortgage_calculate' | 'contact_view' | 'contact_create'
  | 'discussion_view' | 'discussion_create' | 'discussion_reply'
  | 'map_view' | 'map_interact'
  // Engagement
  | 'button_click' | 'modal_open' | 'modal_close' | 'search' | 'filter_apply'
  // Errors
  | 'error' | 'api_error'
  // Custom (prefix with custom_)
  | `custom_${string}`;

interface AnalyticsEvent {
  event_type: EventType;
  event_category?: EventCategory;
  page?: string;
  referrer?: string;
  metadata?: Record<string, any>;
  duration?: number;
}

// Session storage key
const SESSION_KEY = 'fj_analytics_session';

// Get or create session
const getSession = (): { sessionId: string; isNew: boolean } => {
  if (typeof window === 'undefined') {
    return { sessionId: '', isNew: false };
  }

  const stored = sessionStorage.getItem(SESSION_KEY);
  if (stored) {
    try {
      const session = JSON.parse(stored);
      // Session valid for 30 minutes of inactivity
      if (Date.now() - session.lastActivity < 30 * 60 * 1000) {
        session.lastActivity = Date.now();
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
        return { sessionId: session.id, isNew: false };
      }
    } catch {
      // Invalid session, create new
    }
  }

  // Create new session
  const newSession = {
    id: crypto.randomUUID(),
    lastActivity: Date.now(),
  };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(newSession));
  return { sessionId: newSession.id, isNew: true };
};

// Update session activity
const updateSessionActivity = () => {
  if (typeof window === 'undefined') return;
  
  const stored = sessionStorage.getItem(SESSION_KEY);
  if (stored) {
    try {
      const session = JSON.parse(stored);
      session.lastActivity = Date.now();
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } catch {
      // Ignore
    }
  }
};

// Event queue for batching
let eventQueue: AnalyticsEvent[] = [];
let flushTimeout: NodeJS.Timeout | null = null;

// Flush events to server
const flushEvents = async () => {
  if (eventQueue.length === 0) return;
  
  const events = [...eventQueue];
  eventQueue = [];
  
  const { sessionId } = getSession();
  
  try {
    await fetch(`${getApiBaseUrl()}/api/analytics/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        events: events.map(e => ({
          ...e,
          session_id: sessionId,
        })),
      }),
    });
  } catch (error) {
    // Analytics errors should not affect user experience
    logger.debug('Analytics batch failed', 'analytics', { count: events.length });
  }
};

// Schedule flush
const scheduleFlush = () => {
  if (flushTimeout) return;
  
  flushTimeout = setTimeout(() => {
    flushTimeout = null;
    flushEvents();
  }, 2000); // Batch events every 2 seconds
};

// Immediately track a single event
const trackEventImmediate = async (event: AnalyticsEvent) => {
  const { sessionId, isNew } = getSession();
  
  // Start session if new
  if (isNew) {
    try {
      await fetch(`${getApiBaseUrl()}/api/analytics/session/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ entry_page: event.page }),
      });
    } catch {
      // Ignore
    }
  }
  
  try {
    await fetch(`${getApiBaseUrl()}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        ...event,
        session_id: sessionId,
      }),
    });
  } catch {
    // Ignore analytics errors
  }
};

// Queue an event for batching
const queueEvent = (event: AnalyticsEvent) => {
  updateSessionActivity();
  eventQueue.push(event);
  scheduleFlush();
};

export function useAnalytics() {
  const user = useAuthStore(state => state.user);
  const pageStartTime = useRef<number>(Date.now());
  const currentPage = useRef<string>('');

  // Track page leave with duration
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentPage.current) {
        const duration = Date.now() - pageStartTime.current;
        // Use sendBeacon for reliable delivery on page leave
        if (navigator.sendBeacon) {
          const { sessionId } = getSession();
          navigator.sendBeacon(
            `${getApiBaseUrl()}/api/analytics/track`,
            JSON.stringify({
              event_type: 'page_leave',
              event_category: 'navigation',
              page: currentPage.current,
              duration,
              session_id: sessionId,
            })
          );
        }
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, []);

  // Track page view
  const trackPageView = useCallback((page: string, referrer?: string) => {
    // Track previous page leave
    if (currentPage.current && currentPage.current !== page) {
      const duration = Date.now() - pageStartTime.current;
      queueEvent({
        event_type: 'page_leave',
        event_category: 'navigation',
        page: currentPage.current,
        duration,
      });
    }

    // Update current page
    currentPage.current = page;
    pageStartTime.current = Date.now();

    // Track new page view immediately for accuracy
    trackEventImmediate({
      event_type: 'page_view',
      event_category: 'navigation',
      page,
      referrer,
    });
  }, []);

  // Track feature usage
  const trackFeature = useCallback((
    feature: EventType,
    metadata?: Record<string, any>
  ) => {
    // Remove any PII from metadata
    const safeMetadata = { ...metadata };
    delete safeMetadata.email;
    delete safeMetadata.name;
    delete safeMetadata.phone;

    queueEvent({
      event_type: feature,
      event_category: 'feature',
      page: currentPage.current || undefined,
      metadata: safeMetadata,
    });
  }, []);

  // Track generic event
  const trackEvent = useCallback((
    eventType: EventType,
    category: EventCategory = 'general',
    metadata?: Record<string, any>
  ) => {
    const safeMetadata = { ...metadata };
    delete safeMetadata.email;
    delete safeMetadata.name;
    delete safeMetadata.phone;

    queueEvent({
      event_type: eventType,
      event_category: category,
      page: currentPage.current || undefined,
      metadata: safeMetadata,
    });
  }, []);

  // Track error
  const trackError = useCallback((
    error: Error | string,
    context?: Record<string, any>
  ) => {
    const errorMessage = typeof error === 'string' ? error : error.message;
    
    queueEvent({
      event_type: 'error',
      event_category: 'error',
      page: currentPage.current || undefined,
      metadata: {
        error: errorMessage.substring(0, 200), // Truncate long errors
        ...context,
      },
    });
  }, []);

  // Track button/action click
  const trackClick = useCallback((
    buttonName: string,
    metadata?: Record<string, any>
  ) => {
    queueEvent({
      event_type: 'button_click',
      event_category: 'engagement',
      page: currentPage.current || undefined,
      metadata: {
        button: buttonName,
        ...metadata,
      },
    });
  }, []);

  // Track search
  const trackSearch = useCallback((
    query: string,
    resultCount?: number,
    filters?: Record<string, any>
  ) => {
    queueEvent({
      event_type: 'search',
      event_category: 'engagement',
      page: currentPage.current || undefined,
      metadata: {
        // Don't track full query for privacy, just length
        queryLength: query.length,
        resultCount,
        hasFilters: !!filters && Object.keys(filters).length > 0,
      },
    });
  }, []);

  return {
    trackPageView,
    trackFeature,
    trackEvent,
    trackError,
    trackClick,
    trackSearch,
  };
}

export default useAnalytics;

